import { useCallback } from 'react'
import useCameraStream from '../../../hooks/useCameraStream'

type ZoomCapabilities = MediaTrackCapabilities & {
  zoom?: { min: number; max: number; step?: number };
};

const getZoomCapabilities = (track: MediaStreamTrack): ZoomCapabilities | null =>
  (track.getCapabilities?.() as ZoomCapabilities) ?? null;

const useCamera = () => {
  const {
    stream,
    videoTrack,
    facingMode,
    startCamera,
    stopCamera,
    switchCamera,
    error,
  } = useCameraStream({
    initialFacingMode: "environment",
    videoConstraints: { width: { ideal: 1920 }, height: { ideal: 1080 } },
    /* Every fresh stream starts at the widest zoom the device supports. */
    onStreamReady: async (track) => {
      const capabilities = getZoomCapabilities(track);
      if (capabilities?.zoom) {
        await track.applyConstraints({
          advanced: [{ zoom: capabilities.zoom.min } as any]
        });
      }
    },
  });

  const zoomCamera = useCallback(async (zoomLevel: number) => {
    if (!videoTrack) {
      return;
    }

    const capabilities = getZoomCapabilities(videoTrack);

    if (capabilities?.zoom) {
      const clampedZoom = Math.min(
        Math.max(zoomLevel, capabilities.zoom.min),
        capabilities.zoom.max
      );
      await videoTrack.applyConstraints({
        advanced: [ { ...( { zoom: clampedZoom } as any ) } ]
      });
    }
  }, [videoTrack]);

  const capturePhoto = useCallback(async (
    video: HTMLVideoElement | null,
    stopStream: boolean = true
  ): Promise<string | null> => {
    if (!stream || !video) return null;

    const canvas = document.createElement('canvas');

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    canvas.width = videoWidth;
    canvas.height = videoHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, videoWidth, videoHeight, 0, 0, videoWidth, videoHeight);
    }

    const captured = canvas.toDataURL('image/jpeg', 0.95);

    if (stopStream) {
      stopCamera();
    }

    return captured;
  }, [stream, stopCamera]);

  const getZoomInfo = useCallback(() => {
    if (!videoTrack) {
      return null;
    }
    return getZoomCapabilities(videoTrack);
  }, [videoTrack]);

  return { startCamera, zoomCamera, stream, stopCamera, capturePhoto, switchCamera, getZoomInfo, facingMode, error };
}

export default useCamera;
